import { Button, TrashIcon } from "@r360/library";
import React, { useEffect, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { syncStateToBrowserStorage } from "../../configureStore";
import useAppSelector from "../../hooks/useAppSelector";
import useTranslate from "../../hooks/useTranslate";
import { reservationResultSelector, searchSelector } from "../../selectors/Selectors";
import { clearReservationTypeResults } from "../../store/actions/reservationResult";
import { resetLastSearchedDateSpan, resetLastSearchedTimestampByResvtype } from "../../store/actions/search";
import { confirmationDialog } from "../UI";

type TResetSearchButton = {
    activeReservationTypeId?: number;
    onDarkBackground?: boolean;
};

export const ResetSearchButton = ({ activeReservationTypeId, onDarkBackground = true }: TResetSearchButton) => {
    const store = useStore();
    const { lastSearched, lastSearchedDateSpan } = useAppSelector(searchSelector);
    const reservationsResult = useAppSelector(reservationResultSelector);
    const dispatch = useDispatch();
    const t = useTranslate();
    const productsNotAddedPath = useAppSelector(state => state.checkoutCart.productsNotAddedPath);
    let reservationResultCleared = true;
    let lastSearchedCleared = true;
    let lastSearchedDataSpanCleared = true;
    const [resetUrl, setResetUrl] = useState<string>();

    if (activeReservationTypeId !== undefined) {
        reservationResultCleared = !reservationsResult[activeReservationTypeId]?.search;
        lastSearchedCleared = !lastSearched?.[activeReservationTypeId];
        lastSearchedDataSpanCleared = lastSearchedDateSpan.resvtypeId !== activeReservationTypeId;
    }

    useEffect(() => {
        if (resetUrl && reservationResultCleared && lastSearchedCleared && lastSearchedDataSpanCleared) {
            syncStateToBrowserStorage(store)();
            window.location.href = resetUrl; // Triggers a full reload after 1s.
        }
    }, [resetUrl, reservationResultCleared, lastSearchedCleared, lastSearchedDataSpanCleared, store]);

    const handleReset = async () => {
        // If there are products not added to cart, show a warning dialog.
        if (productsNotAddedPath) {
            const response = await confirmationDialog({
                heading: t("book.leave_warning_header"),
                description: t("book.leave_warning_text"),
                confirmBtnText: t("book.leave_warning_confirm"),
                cancelBtnText: t("book.leave_warning_cancel"),
            });

            if (!response) {
                return;
            }
        }

        dispatch(resetLastSearchedTimestampByResvtype(activeReservationTypeId));
        dispatch(resetLastSearchedDateSpan());
        dispatch(clearReservationTypeResults(activeReservationTypeId));

        const url = new URL(window.location.href);
        url.search = ""; // Removes all query params.
        setResetUrl(url.toString());
    };

    return (
        <Button type="tertiary" customColorsReversed={onDarkBackground} onClick={handleReset} leftIcon={<TrashIcon />}>
            {t("book.search.clear_search")}
        </Button>
    );
};
